/*
 * The three layouts and the widths they allow (spec 12 §4.2). They are pixel numbers rather than
 * percentages because that is what the panes actually need: a member list stops working below
 * 300 px whatever the window does.
 */

import { createContext, useContext, useEffect, useSyncExternalStore } from "react"
import { readJSON, storageKey } from "../../lib/storage.ts"

export type LayoutMode = "wide" | "medium" | "narrow"

/** Three panes from here up; below it the details pane becomes a sheet. */
export const WIDE_FROM_PX = 1200
/** Below this it is one pane per route and the rail becomes a bottom bar. */
export const MEDIUM_FROM_PX = 768

export const SIDEBAR = { min: 220, max: 420, default: 256 }
/** Wider than a member list needs: a thread in this column carries a composer (UX §2.2). */
export const DETAILS = { min: 320, max: 560, default: 360 }

export const SIDEBAR_WIDTH_KEY = storageKey("paneWidths", "sidebar")
export const DETAILS_WIDTH_KEY = storageKey("paneWidths", "details")

/** A pane's bounds and the width it has before anybody drags it. */
export interface PaneWidths {
	min: number
	max: number
	default: number
}

/**
 * The width a pane opens at: the one the person last dragged it to, within its bounds, or its
 * default. The pane itself starts from this (`ResizablePanel.tsx`), and a screen that must know how
 * wide the details column will be before it opens asks `detailsWidthNow`.
 */
export function storedWidth(key: string, bounds: PaneWidths): number {
	const stored = readJSON<number>(key)
	return typeof stored === "number"
		? Math.min(bounds.max, Math.max(bounds.min, Math.round(stored)))
		: bounds.default
}

/** The width the details column opens at now (`storedWidth`). */
export function detailsWidthNow(): number {
	return storedWidth(DETAILS_WIDTH_KEY, DETAILS)
}

/** The id the composer carries, so the shell's second skip link has somewhere to land. */
export const COMPOSER_ID = "tb-composer"
/** The id of the main landmark, the target of the first skip link. */
export const MAIN_ID = "tb-main"
/** The id of the sidebar's region, which a control that folds it names in `aria-controls`. */
export const SIDEBAR_ID = "tb-sidebar"

/**
 * How long the caret waits for a composer that is on its way, and how many frames it has to hold
 * there before the attempt is over. One frame is not enough: the box is re-created when its draft
 * arrives from the disk, and the focus goes with the view it was given to.
 */
const FOCUS_BUDGET_MS = 3_000
const FOCUS_SETTLE_FRAMES = 3

/**
 * Puts the caret into the message box of the conversation that is opening — what *Open #allgemein* on
 * the first-run screen and a forwarded message both want, so that the first keystroke after the
 * navigation lands where the reader is looking.
 *
 * The box arrives a frame or two after the navigation — a route mounts, a window is fetched — so it is
 * waited for rather than assumed, and the wait is bounded: a navigation that did not end in a
 * conversation leaves the focus where it was instead of hunting for a box that is never coming.
 *
 * It runs outside React on purpose: the surface that started the navigation is unmounted by it, and an
 * effect of a component that is gone is cancelled with it.
 */
export function focusComposerWhenItArrives(budgetMs: number = FOCUS_BUDGET_MS): void {
	const deadline = Date.now() + budgetMs
	let held = 0
	const look = (): void => {
		const box = document
			.getElementById(COMPOSER_ID)
			?.querySelector<HTMLElement>(".tb-composer__input")
		const active = document.activeElement
		if (box !== null && box !== undefined) {
			if (active !== null && box.contains(active)) {
				if (++held >= FOCUS_SETTLE_FRAMES) return
			} else {
				held = 0
				box.focus()
			}
		}
		if (Date.now() < deadline) requestAnimationFrame(look)
	}
	requestAnimationFrame(look)
}

function modeFor(width: number): LayoutMode {
	if (width >= WIDE_FROM_PX) return "wide"
	if (width >= MEDIUM_FROM_PX) return "medium"
	return "narrow"
}

/**
 * One answer read off the window's width. It is a primitive — a mode, a yes or a no — so a resize
 * re-renders nothing until a breakpoint is crossed, and it is read in the render itself rather than
 * after it: a question that changes with the route (a place whose column folds) is answered in the
 * same frame as the route, never one painted frame late.
 */
function useWidthAnswer<T extends string | boolean>(read: (width: number) => T): T {
	return useSyncExternalStore(
		subscribeToResize,
		() => read(window.innerWidth),
		() => read(WIDE_FROM_PX),
	)
}

function subscribeToResize(onChange: () => void): () => void {
	window.addEventListener("resize", onChange)
	return () => window.removeEventListener("resize", onChange)
}

/**
 * The current layout. It follows the window rather than a container query because the rail, the
 * sidebar and the details pane are siblings: they have to agree on one answer.
 */
export function useLayoutMode(): LayoutMode {
	return useWidthAnswer(modeFor)
}

/**
 * Whether the window is at least `px` wide — the one other width question the frame asks, for a
 * column that folds below a width its place names (`SidebarSection.fold`). Undefined asks nothing
 * and answers true: a column without a fold always has room.
 */
export function useWindowAtLeast(px: number | undefined): boolean {
	return useWidthAnswer((width) => px === undefined || width >= px)
}

/**
 * Below 768 px the rail is a bar along the foot of the screen, and 56 px of a phone's height is a
 * lot to give up while the keyboard is up. A screen whose whole height is somebody's writing — a
 * document being edited on a phone (G-02 M11) — asks the bar to step aside for as long as it says
 * so, and the page's own band carries the way back. The frame counts the requests, so two screens
 * that ask never undo one another, and a screen that is gone has withdrawn its own.
 */
export const BottomBarContext = createContext<() => () => void>(() => () => undefined)

/**
 * Hides the bottom bar while `active` is true and this component is mounted. It changes nothing
 * above 768 px, where there is no bar.
 */
export function useHideBottomBar(active: boolean): void {
	const stepAside = useContext(BottomBarContext)
	useEffect(() => (active ? stepAside() : undefined), [active, stepAside])
}
